/**
 * Shared sample × room preview engine for Liminal Reverb product-page UIs.
 *
 * One playing pair at a time: sampleIndex + roomIndex.
 * Files are decoded once, then played from memory. Room and dry switches
 * keep the playhead and crossfade about 60ms. Sample changes restart.
 * Persists last choice in sessionStorage. Play only after unlock() (user gesture).
 *
 * Register files in assets/lr-room-library.js (sampleId × roomId → filename).
 */
(function (global) {
  var STORAGE_KEY = 'lr-preview-pair-v1';
  var ROOM_FADE = 0.06;

  function library() {
    return global.LR_ROOM_DEMOS || { samples: [], rooms: [] };
  }

  function clamp(n, a, b) {
    return n < a ? a : n > b ? b : n;
  }

  function resolveAssetUrl(base, file) {
    if (file == null || file === '') return '';
    var value = String(file).trim();
    if (!value || value === 'null') return '';
    if (/^https?:\/\//i.test(value) || value.indexOf('//') === 0 || value.charAt(0) === '/') {
      return value;
    }
    return (base || '') + value;
  }

  function LiminalPreviewEngine(opts) {
    opts = opts || {};
    this.assetBase = opts.assetBase || '';
    this.storageKey = opts.storageKey || STORAGE_KEY;
    this.roomFade = ROOM_FADE;
    this.reduced = global.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.sampleIndex = 0;
    this.roomIndex = 0;
    this.dry = false;
    this.playing = false;
    this.loading = false;
    this.unlocked = false;
    this.userVolume = 0.7;
    this.token = 0;
    this.listeners = {};
    this.buffers = {};
    this.pending = {};
    this.voice = null;
    this.fading = null;
    this.clock = null;
    this.position = 0;
    this.startedAt = 0;
    this.offset = 0;
    /* Stand-in so skins can read engine.active.currentTime. */
    this.active = { currentTime: 0, duration: 0, paused: true };

    this._restore();
  }

  LiminalPreviewEngine.prototype.on = function (event, fn) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(fn);
    return this;
  };

  LiminalPreviewEngine.prototype.emit = function (event, payload) {
    var list = this.listeners[event] || [];
    var data = payload != null ? payload : this.getState();
    for (var i = 0; i < list.length; i++) list[i](data);
  };

  LiminalPreviewEngine.prototype.samples = function () {
    return library().samples || [];
  };

  LiminalPreviewEngine.prototype.rooms = function () {
    return library().rooms || [];
  };

  LiminalPreviewEngine.prototype.sample = function () {
    return this.samples()[this.sampleIndex] || null;
  };

  LiminalPreviewEngine.prototype.room = function () {
    return this.rooms()[this.roomIndex] || null;
  };

  LiminalPreviewEngine.prototype.slot = function () {
    var room = this.room();
    var sample = this.sample();
    if (!room || !sample || !room.samples) return null;
    for (var i = 0; i < room.samples.length; i++) {
      if (room.samples[i].id === sample.id) return room.samples[i];
    }
    return null;
  };

  LiminalPreviewEngine.prototype.urlAt = function (sampleIndex, roomIndex, dry) {
    var samples = this.samples();
    var rooms = this.rooms();
    var sample = samples[sampleIndex];
    var room = rooms[roomIndex];
    if (!sample) return '';
    if (dry) return resolveAssetUrl(this.assetBase, sample.dry);
    if (!room || !room.samples) return '';
    for (var i = 0; i < room.samples.length; i++) {
      if (room.samples[i].id === sample.id) {
        return resolveAssetUrl(this.assetBase, room.samples[i].audio);
      }
    }
    return '';
  };

  LiminalPreviewEngine.prototype.currentUrl = function () {
    return this.urlAt(this.sampleIndex, this.roomIndex, this.dry);
  };

  LiminalPreviewEngine.prototype.artworkUrl = function (roomIndex) {
    var room = this.rooms()[roomIndex != null ? roomIndex : this.roomIndex];
    return room ? resolveAssetUrl(this.assetBase, room.artwork) : '';
  };

  LiminalPreviewEngine.prototype.artworkAltUrl = function (roomIndex) {
    var room = this.rooms()[roomIndex != null ? roomIndex : this.roomIndex];
    return room ? resolveAssetUrl(this.assetBase, room.artworkAlt) : '';
  };

  LiminalPreviewEngine.prototype.getState = function () {
    var sample = this.sample();
    var room = this.room();
    var url = this.currentUrl();
    return {
      sampleIndex: this.sampleIndex,
      roomIndex: this.roomIndex,
      dry: this.dry,
      sample: sample,
      room: room,
      url: url,
      hasAudio: !!url,
      playing: this.playing,
      loading: this.loading,
      unlocked: this.unlocked,
      volume: this.userVolume,
      artwork: this.artworkUrl(),
      currentTime: this._now(),
      duration: this._duration()
    };
  };

  LiminalPreviewEngine.prototype._persist = function () {
    try {
      sessionStorage.setItem(
        this.storageKey,
        JSON.stringify({
          sampleIndex: this.sampleIndex,
          roomIndex: this.roomIndex,
          dry: this.dry,
          volume: this.userVolume
        })
      );
    } catch (err) {
      /* private mode */
    }
  };

  LiminalPreviewEngine.prototype._restore = function () {
    try {
      var raw = sessionStorage.getItem(this.storageKey);
      if (!raw) return;
      var data = JSON.parse(raw);
      var sMax = this.samples().length - 1;
      var rMax = this.rooms().length - 1;
      if (typeof data.sampleIndex === 'number') {
        this.sampleIndex = clamp(data.sampleIndex, 0, Math.max(0, sMax));
      }
      if (typeof data.roomIndex === 'number') {
        this.roomIndex = clamp(data.roomIndex, 0, Math.max(0, rMax));
      }
      if (typeof data.volume === 'number') {
        this.userVolume = clamp(data.volume, 0, 1);
      }
      this.dry = data.dry === true;
    } catch (err) {
      /* ignore */
    }
  };

  LiminalPreviewEngine.prototype.unlock = function () {
    this.unlocked = true;
    this._ensureCtx();
  };

  LiminalPreviewEngine.prototype._ensureCtx = function () {
    var Ctx = global.AudioContext || global.webkitAudioContext;
    if (!Ctx) return null;
    try {
      if (!this.audioCtx) {
        this.audioCtx = new Ctx();
        this.master = this.audioCtx.createGain();
        this.master.connect(this.audioCtx.destination);
      }
      this.master.gain.value = this.userVolume;
      if (this.audioCtx.state === 'suspended') this.audioCtx.resume();
      return this.audioCtx;
    } catch (err) {
      this.audioCtx = null;
      return null;
    }
  };

  LiminalPreviewEngine.prototype.setVolume = function (v) {
    this.userVolume = clamp(v, 0, 1);
    if (this.master && this.audioCtx) {
      this.master.gain.setValueAtTime(this.userVolume, this.audioCtx.currentTime);
    }
    this._persist();
    this.emit('change');
  };

  LiminalPreviewEngine.prototype._now = function () {
    if (!this.playing || !this.voice || !this.audioCtx) return this.position || 0;
    var t = this.audioCtx.currentTime - this.startedAt + this.offset;
    var dur = this.voice.buffer ? this.voice.buffer.duration : 0;
    if (t < 0) return 0;
    if (dur && t > dur) return dur;
    return t;
  };

  LiminalPreviewEngine.prototype._duration = function () {
    var url = this.currentUrl();
    var buf = url && this.buffers[url];
    if (buf && buf.duration) return buf.duration;
    if (this.voice && this.voice.buffer) return this.voice.buffer.duration;
    return this.active && this.active.duration ? this.active.duration : 0;
  };

  LiminalPreviewEngine.prototype._sampleUrls = function (sampleIndex) {
    var urls = [];
    var dry = this.urlAt(sampleIndex, 0, true);
    if (dry) urls.push(dry);
    var rooms = this.rooms();
    for (var i = 0; i < rooms.length; i++) {
      var url = this.urlAt(sampleIndex, i, false);
      if (url && urls.indexOf(url) === -1) urls.push(url);
    }
    return urls;
  };

  LiminalPreviewEngine.prototype._decode = function (url) {
    var self = this;
    if (!url) return Promise.reject(new Error('empty'));
    if (this.buffers[url]) return Promise.resolve(this.buffers[url]);
    if (this.pending[url]) return this.pending[url];
    var ctx = this._ensureCtx();
    if (!ctx) return Promise.reject(new Error('no audio'));
    this.pending[url] = fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('fetch');
        return res.arrayBuffer();
      })
      .then(function (raw) {
        return ctx.decodeAudioData(raw.slice(0));
      })
      .then(function (buf) {
        var keep = self._sampleUrls(self.sampleIndex);
        if (keep.indexOf(url) !== -1) self.buffers[url] = buf;
        delete self.pending[url];
        return buf;
      })
      .catch(function (err) {
        delete self.pending[url];
        throw err;
      });
    return this.pending[url];
  };

  /** Decode the other rooms (and the dry take) for the current sample. */
  LiminalPreviewEngine.prototype._warm = function () {
    var urls = this._sampleUrls(this.sampleIndex);
    var keep = {};
    for (var i = 0; i < urls.length; i++) {
      keep[urls[i]] = true;
      if (!this.buffers[urls[i]]) this._decode(urls[i]).catch(function () {});
    }
    for (var key in this.buffers) {
      if (!keep[key]) delete this.buffers[key];
    }
  };

  LiminalPreviewEngine.prototype.setSample = function (index) {
    var max = this.samples().length - 1;
    if (max < 0) return;
    index = clamp(index, 0, max);
    if (index === this.sampleIndex) return;
    var wasPlaying = this.playing || this.loading;
    this.sampleIndex = index;
    this.position = 0;
    this.active.currentTime = 0;
    this._persist();
    this.emit('change');
    if (wasPlaying) this._go(false);
    else this.prefetchNeighbors();
  };

  LiminalPreviewEngine.prototype.setRoom = function (index) {
    var max = this.rooms().length - 1;
    if (max < 0) return;
    index = clamp(index, 0, max);
    if (index === this.roomIndex) return;
    var wasPlaying = this.playing || this.loading;
    this.roomIndex = index;
    this.position = this._now();
    this._persist();
    this.emit('change');
    this.emit('roomcut', this.getState());
    if (wasPlaying) this._go(true);
    else this.prefetchNeighbors();
  };

  /** Bypass the room: play the unprocessed source for the current sample. */
  LiminalPreviewEngine.prototype.setDry = function (on) {
    on = !!on;
    if (on === this.dry) return;
    var wasPlaying = this.playing || this.loading;
    this.dry = on;
    this.position = this._now();
    this._persist();
    this.emit('change');
    this.emit('drycut', this.getState());
    if (wasPlaying) this._go(true);
    else this.prefetchNeighbors();
  };

  LiminalPreviewEngine.prototype.toggle = function () {
    if (this.playing || this.loading) this.pause();
    else this.play();
  };

  LiminalPreviewEngine.prototype.pause = function () {
    this.token++;
    this.position = this._now();
    this.playing = false;
    this.loading = false;
    this.active.currentTime = this.position;
    this.active.paused = true;
    this._halt(true);
    this._stopClock();
    this.emit('change');
  };

  LiminalPreviewEngine.prototype.play = function () {
    if (!this.unlocked) this.unlock();
    this._go(true);
  };

  LiminalPreviewEngine.prototype.seek = function (t) {
    var dur = this._duration();
    if (!(dur > 0)) return;
    t = clamp(Math.max(0, t), 0, Math.max(0, dur - 0.01));
    this.position = t;
    this.active.currentTime = t;
    if (this.playing && this.voice && this.voice.buffer) {
      this._spawn(this.voice.buffer, this.voice.url, t, false);
    } else {
      this.emit('time', { current: t, duration: dur });
    }
  };

  /**
   * keepTime: room and dry changes stay on the timeline.
   * A sample change passes false and starts at the beginning.
   */
  LiminalPreviewEngine.prototype._go = function (keepTime) {
    var self = this;
    var token = ++this.token;
    var url = this.currentUrl();

    if (!url) {
      this._halt(false);
      this.playing = false;
      this.loading = false;
      this._stopClock();
      this.emit('change');
      return;
    }

    var at = keepTime ? this._now() : 0;
    if (!keepTime) {
      this.position = 0;
      this.active.currentTime = 0;
      this._halt(false);
    }

    this._warm();
    var cached = this.buffers[url];
    if (cached) {
      this._spawn(cached, url, at, !!(keepTime && this.voice));
      return;
    }

    var stillPlaying = keepTime && !!this.voice;
    if (!stillPlaying) {
      this.playing = false;
      this.loading = true;
      this.emit('change');
    }

    this._decode(url).then(function (buf) {
      if (token !== self.token) return;
      var live = keepTime ? self._now() : 0;
      self._spawn(buf, url, live, !!(keepTime && self.voice));
      self._warm();
    }).catch(function () {
      if (token !== self.token) return;
      if (!self.voice) {
        self.loading = false;
        self.playing = false;
        self.emit('change');
      }
    });
  };

  LiminalPreviewEngine.prototype._spawn = function (buffer, url, at, fadeIn) {
    var ctx = this._ensureCtx();
    if (!ctx || !buffer) return;
    var now = ctx.currentTime;
    var fadeSec = this.reduced ? 0.012 : this.roomFade;
    if (!(at > 0)) at = 0;
    if (buffer.duration && at >= buffer.duration - 0.03) at = 0;

    if (this.fading) {
      this.fading.stopped = true;
      try { this.fading.source.stop(); } catch (err) { /* already stopped */ }
      this.fading = null;
    }

    var fade = ctx.createGain();
    fade.connect(this.master);
    var src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(fade);

    var prev = this.voice;
    if (prev && fadeIn) {
      fade.gain.setValueAtTime(0, now);
      fade.gain.linearRampToValueAtTime(1, now + fadeSec);
    } else {
      fade.gain.setValueAtTime(1, now);
    }

    try {
      src.start(now, at);
    } catch (err) {
      return;
    }

    if (prev && fadeIn) {
      prev.stopped = true;
      this.fading = prev;
      try {
        prev.fade.gain.cancelScheduledValues(now);
        prev.fade.gain.setValueAtTime(prev.fade.gain.value, now);
        prev.fade.gain.linearRampToValueAtTime(0, now + fadeSec);
        prev.source.stop(now + fadeSec + 0.02);
      } catch (err2) { /* already stopped */ }
    } else if (prev) {
      prev.stopped = true;
      try { prev.source.stop(); } catch (err3) { /* already stopped */ }
    }

    var voice = {
      source: src,
      fade: fade,
      url: url,
      buffer: buffer,
      stopped: false
    };
    this.voice = voice;
    this.offset = at;
    this.startedAt = now;
    this.playing = true;
    this.loading = false;
    this.position = at;
    this.active.currentTime = at;
    this.active.duration = buffer.duration || 0;
    this.active.paused = false;

    var self = this;
    src.onended = function () {
      if (voice.stopped || self.voice !== voice) return;
      self._finish(buffer.duration || 0);
    };

    this._startClock();
    this.emit('time', { current: at, duration: buffer.duration || 0 });
    this.emit('change');
  };

  LiminalPreviewEngine.prototype._halt = function (fadeOut) {
    if (this.fading) {
      this.fading.stopped = true;
      try { this.fading.source.stop(); } catch (err) { /* already stopped */ }
      this.fading = null;
    }
    var voice = this.voice;
    if (!voice) return;
    this.voice = null;
    voice.stopped = true;
    if (!fadeOut || !this.audioCtx) {
      try { voice.source.stop(); } catch (err) { /* already stopped */ }
      return;
    }
    var now = this.audioCtx.currentTime;
    try {
      voice.fade.gain.cancelScheduledValues(now);
      voice.fade.gain.setValueAtTime(voice.fade.gain.value, now);
      voice.fade.gain.linearRampToValueAtTime(0, now + 0.03);
      voice.source.stop(now + 0.04);
    } catch (err) {
      try { voice.source.stop(); } catch (e2) { /* already stopped */ }
    }
  };

  LiminalPreviewEngine.prototype._finish = function (duration) {
    if (!this.playing && !this.voice) return;
    var voice = this.voice;
    this.playing = false;
    this.loading = false;
    this.position = 0;
    this.voice = null;
    this.active.currentTime = 0;
    this.active.paused = true;
    this._stopClock();
    if (voice) {
      voice.stopped = true;
      try { voice.source.stop(); } catch (err) { /* already ended */ }
    }
    this.emit('time', { current: 0, duration: duration || 0 });
    this.emit('change');
  };

  LiminalPreviewEngine.prototype._startClock = function () {
    var self = this;
    if (this.clock) return;
    this.clock = setInterval(function () {
      if (!self.playing || !self.voice) return;
      var current = self._now();
      var duration = self.voice.buffer ? self.voice.buffer.duration : 0;
      self.position = current;
      self.active.currentTime = current;
      self.active.duration = duration || 0;
      self.emit('time', { current: current, duration: duration || 0 });
      if (duration && current >= duration) self._finish(duration);
    }, 80);
  };

  LiminalPreviewEngine.prototype._stopClock = function () {
    if (!this.clock) return;
    clearInterval(this.clock);
    this.clock = null;
  };

  LiminalPreviewEngine.prototype.prefetchNeighbors = function () {
    if (!this.unlocked) return;
    this._warm();
  };

  /** Soft elevator ding — optional cue on room change (skin #3). */
  LiminalPreviewEngine.prototype.playDing = function () {
    if (!this.unlocked) return;
    try {
      var ctx = this._ensureCtx();
      if (!ctx) return;
      var osc = ctx.createOscillator();
      var gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.value = 0.0001;
      osc.connect(gain);
      gain.connect(ctx.destination);
      var now = ctx.currentTime;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      osc.start(now);
      osc.stop(now + 0.4);
    } catch (err) {
      /* ignore */
    }
  };

  global.LiminalPreviewEngine = LiminalPreviewEngine;
})(window);
